"use client"
import React, { useContext, useEffect, useState } from "react"
import { AuthContext } from "../../context/AuthContext"
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from "recharts"
import {
    TrendingUp,
    Truck,
    Layers,
    Clock,
    AlertCircle,
    CreditCard,
} from "lucide-react"

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000"
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"]

const StoreInsights = () => {
    const { token } = useContext(AuthContext)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Filter State
    const [dateRange, setDateRange] = useState("30days")
    const [channel, setChannel] = useState("All")

    const calculateDateRange = (range) => {
        const end = new Date()
        let start = new Date()

        switch (range) {
            case "today":
                start.setHours(0, 0, 0, 0)
                end.setHours(23, 59, 59, 999)
                break
            case "yesterday":
                start.setDate(start.getDate() - 1)
                start.setHours(0, 0, 0, 0)
                end.setDate(end.getDate() - 1)
                end.setHours(23, 59, 59, 999)
                break
            case "7days":
                start.setDate(start.getDate() - 7)
                break
            case "30days":
                start.setDate(start.getDate() - 30)
                break
            case "thisMonth":
                start.setDate(1)
                break
            case "lastMonth":
                start.setMonth(start.getMonth() - 1)
                start.setDate(1)
                end.setDate(0) // Last day of previous month
                break
            case "all":
                return { startDate: null, endDate: null }
            default:
                start.setDate(start.getDate() - 30)
        }
        return { startDate: start.toISOString(), endDate: end.toISOString() }
    }

    const getDateLabel = (range) => {
        switch (range) {
            case "today": return "(Today)"
            case "yesterday": return "(Yesterday)"
            case "7days": return "(Last 7 Days)"
            case "30days": return "(Last 30 Days)"
            case "thisMonth": return "(This Month)"
            case "lastMonth": return "(Last Month)"
            case "all": return "(All Time)"
            default: return "(Last 30 Days)"
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const { startDate, endDate } = calculateDateRange(dateRange)

                const params = new URLSearchParams()
                if (startDate) params.append("startDate", startDate)
                if (endDate) params.append("endDate", endDate)
                if (channel !== "All") params.append("channel", channel)

                const res = await fetch(`${BASE_URL}/api/dashboard/store-insights?${params.toString()}`, {
                    headers: { "x-auth-token": token },
                })
                if (!res.ok) throw new Error("Failed to fetch store insights")
                const jsonData = await res.json()
                setData(jsonData)
            } catch (err) {
                console.error("Store Insights error:", err)
                setError("Failed to load business intelligence data.")
            } finally {
                setLoading(false)
            }
        }
        if (token) fetchData()
    }, [token, dateRange, channel])

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mr-2"></div>
            analyzing store data...
        </div>
    )

    if (error) return (
        <div className="flex flex-col items-center justify-center min-h-screen text-red-500">
            <AlertCircle className="w-12 h-12 mb-4" />
            <p>{error}</p>
            <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-red-100 rounded-md hover:bg-red-200 text-red-700">Retry</button>
        </div>
    )

    if (!data) return null

    const { channelPerformance, categoryPerformance, shippingFinancials, peakTimes } = data

    // Prepare Heatmap Grid for Peak Times
    // 7 days x 24 hours
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const hours = Array.from({ length: 24 }, (_, i) => i) // 0-23

    const getHeatmapIntensity = (dayIso, hour) => {
        // dayIso: 1 (Sun) to 7 (Sat)
        const found = peakTimes.find(pt => pt._id.day === dayIso && pt._id.hour === hour)
        return found ? found.count : 0
    }

    const maxPeak = Math.max(...peakTimes.map(pt => pt.count), 1)

    return (
        <div className="p-6 bg-gray-50 min-h-screen max-w-7xl mx-auto space-y-8">
            {/* Header & Filters */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <Layers className="mr-2 text-indigo-600" /> Store Performance
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Deep dive into channels, categories, and operational efficiency.</p>
                </div>

                <div className="flex flex-wrap gap-3">
                    {/* Channel Filter */}
                    <div className="relative">
                        <select
                            value={channel}
                            onChange={(e) => setChannel(e.target.value)}
                            className="appearance-none bg-white border border-gray-200 text-gray-700 py-2 pl-3 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm cursor-pointer"
                        >
                            <option value="All">All Channels</option>
                            <option value="Facebook">Facebook</option>
                            <option value="WhatsApp">WhatsApp</option>
                            <option value="Instagram">Instagram</option>
                            <option value="Web">Web</option>
                            <option value="Phone">Phone</option>
                            <option value="Other">Other</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                        </div>
                    </div>

                    {/* Date Filter */}
                    <div className="relative">
                        <Clock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="appearance-none bg-white border border-gray-200 text-gray-700 py-2 pl-9 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm cursor-pointer"
                        >
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="7days">Last 7 Days</option>
                            <option value="30days">Last 30 Days</option>
                            <option value="thisMonth">This Month</option>
                            <option value="lastMonth">Last Month</option>
                            <option value="all">All Time</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI Row - Financial Efficiency */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                    title="Shipping Revenue"
                    value={`Rs. ${(shippingFinancials.totalCharged / 1000).toFixed(1)}k`}
                    subtext="Amount charged to customers"
                    icon={<CreditCard className="text-blue-600" />}
                    bg="bg-blue-50"
                />
                <MetricCard
                    title="Shipping Cost"
                    value={`Rs. ${(shippingFinancials.totalCost / 1000).toFixed(1)}k`}
                    subtext="Actual cost paid to couriers"
                    icon={<Truck className="text-amber-600" />}
                    bg="bg-amber-50"
                />
                <MetricCard
                    title="Net Shipping Margin"
                    value={`Rs. ${((shippingFinancials.totalCharged - shippingFinancials.totalCost) / 1000).toFixed(1)}k`}
                    subtext={shippingFinancials.totalCharged >= shippingFinancials.totalCost ? "Profit on shipping" : "Subsidizing shipping"}
                    icon={<TrendingUp className={shippingFinancials.totalCharged >= shippingFinancials.totalCost ? "text-green-600" : "text-red-600"} />}
                    bg={shippingFinancials.totalCharged >= shippingFinancials.totalCost ? "bg-green-50" : "bg-red-50"}
                    valueColor={shippingFinancials.totalCharged >= shippingFinancials.totalCost ? "text-green-700" : "text-red-700"}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Channel Performance - Stacked/Simple Bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-[400px] flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Sales by Channel</h3>
                    <p className="text-xs text-gray-500 mb-6">Revenue breakdown by platform {getDateLabel(dateRange)}</p>
                    <div className="flex-grow">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={channelPerformance} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(val) => `Rs.${val / 1000}k`} />
                                <YAxis type="category" dataKey="_id" width={80} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151', fontWeight: 500 }} />
                                <Tooltip
                                    cursor={{ fill: '#F3F4F6' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                    formatter={(val) => [`Rs. ${val.toLocaleString()}`, 'Revenue']}
                                />
                                <Bar dataKey="totalSales" name="Revenue" radius={[0, 4, 4, 0]} barSize={32}>
                                    {channelPerformance.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Category Performance - Horizontal Bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-[400px] flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Top Product Categories</h3>
                    <p className="text-xs text-gray-500 mb-6">Revenue by product type {getDateLabel(dateRange)}</p>
                    <div className="flex-grow">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={categoryPerformance} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" />
                                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} tickFormatter={(val) => `Rs.${val / 1000}k`} />
                                <YAxis type="category" dataKey="_id" width={100} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#374151' }} />
                                <Tooltip
                                    cursor={{ fill: '#F3F4F6' }}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                    formatter={(val) => [`Rs. ${val.toLocaleString()}`, 'Revenue']}
                                />
                                <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} barSize={20} fill="#8884d8">
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Peak Ordering Times Grid */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Peak Ordering Times</h3>
                        <p className="text-xs text-gray-500">Heatmap of order volume by Day and Hour {getDateLabel(dateRange)}</p>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <span>Less</span>
                        <div className="flex space-x-1">
                            <div className="w-3 h-3 bg-indigo-50 rounded-sm"></div>
                            <div className="w-3 h-3 bg-indigo-200 rounded-sm"></div>
                            <div className="w-3 h-3 bg-indigo-400 rounded-sm"></div>
                            <div className="w-3 h-3 bg-indigo-600 rounded-sm"></div>
                        </div>
                        <span>More</span>
                    </div>
                </div>

                <div className="overflow-x-auto pb-2">
                    <div className="min-w-[800px]">
                        {/* Header Row (Hours) */}
                        <div className="flex">
                            <div className="w-16 flex-shrink-0"></div>
                            {hours.map(h => (
                                <div key={h} className="flex-1 text-center text-xs text-gray-400 font-mono">
                                    {h === 0 ? '12am' : h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am`}
                                </div>
                            ))}
                        </div>

                        {/* Grid Rows (Days) */}
                        {days.map((day, dayIndex) => { // dayIndex 0-6
                            const dayIso = dayIndex + 1 // MongoDB dayOfWeek is 1-7 (Sun-Sat)
                            return (
                                <div key={day} className="flex items-center mt-1">
                                    <div className="w-16 flex-shrink-0 text-xs font-semibold text-gray-600">{day}</div>
                                    {hours.map(h => {
                                        const intensity = getHeatmapIntensity(dayIso, h)
                                        const ratio = intensity / maxPeak

                                        let bgClass = "bg-gray-50"
                                        if (intensity > 0) {
                                            if (ratio < 0.25) bgClass = "bg-indigo-100"
                                            else if (ratio < 0.5) bgClass = "bg-indigo-300"
                                            else if (ratio < 0.75) bgClass = "bg-indigo-500"
                                            else bgClass = "bg-indigo-700"
                                        }

                                        return (
                                            <div key={`${day}-${h}`} className="flex-1 h-8 p-0.5" title={`${day} ${h}:00 - ${intensity} orders`}>
                                                <div className={`w-full h-full rounded-sm ${bgClass} transition-all hover:opacity-80`}></div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

        </div>
    )
}

const MetricCard = ({ title, value, subtext, icon, bg, valueColor = "text-gray-900" }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-start space-x-4">
        <div className={`p-3 rounded-lg ${bg} flex-shrink-0 children:w-6 children:h-6`}>
            {icon}
        </div>
        <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <h3 className={`text-2xl font-bold ${valueColor} mt-1`}>{value}</h3>
            <p className="text-xs text-gray-400 mt-1">{subtext}</p>
        </div>
    </div>
)

export default StoreInsights
